/* ============================================================================
   TPW GENIUS BAR  (assets/tpw-genius-bar.js)  —  v2
   ----------------------------------------------------------------------------
   A slim DARK, context-aware converter ribbon pinned to the top of every page.
   ONE short animated line at a time; it KNOWS the shopper (visit, page, product,
   cart, Melbourne time) and says the single most useful thing — converting,
   genuinely helpful, or delightful, never filler.

   SIGNATURE MOTION (founder-approved): the whole message rolls up word-by-word
   (per-word vertical cascade) and every number settles on a vertical odometer
   reel. The live CONVERSION number (the dispatch countdown, the $ to free
   shipping, the promo %) stands out by BRIGHTNESS + WEIGHT — pure white #FFFFFF
   at 600 vs the warm-paper #F6F4EE rest (founder dropped the old red accent,
   2026-06-26). Facts (7pm, $150, 5.0, capacities) stay paper.

   v2 adds: richer 4-6 line per-page decks, per-message dwell timing, a TAPPABLE
   concierge (help lines link to our Melbourne team / contact), type-aware PRODUCT
   INTELLIGENCE (a true line per product_type, upgradeable to spec numbers via a
   pre-resolved data-pi), mobile tight-variants, and staged-promo plumbing (OFF
   until a real discount code + toggle are supplied).

   Strategy: CRO v2 blueprint + Cicero copy. Priority cascade:
     1. cart reaction (added / removed)          ~4.8s, then re-resolve
     2. free shipping unlocked (>= $150)          PIN
     3. free shipping progress (< $150)           PIN  ("only $X away")
     4. live promo offer (eligible + toggle ON)   front of deck, one reveal
     5. empty cart -> rotate the page deck (converter line recomputed live)

   Dispatch/store-hours come ONLY from window.TPWStoreHours. Cart engine is the
   proven one from the parked tpw-bar.js. Native class extends HTMLElement.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__tpwGeniusBar) return;
  window.__tpwGeniusBar = true;

  var THRESHOLD = 15000;          // free shipping at $150 (cents)
  var NEAR = 2000;                // "near" = within $20 of the threshold
  var STORAGE_KEY = '__tpw_gb_cache_v1';
  var STORAGE_TTL = 5 * 60 * 1000;
  var POLL_MS = 30000;
  var DEBOUNCE_MS = 100;
  var REACT_MS = 4800;            // add/remove reaction holds ~4.8s
  var RESUME_MS = 5200;           // cart-resume greeting holds ~5.2s
  // Founder 2026-06-26: dropped the red accent in the bar — disliked. The live
  // conversion number now stands out by BRIGHTNESS + WEIGHT on the dark bar
  // (pure white vs the warm-paper #F6F4EE rest), the monochrome way, not by hue.
  var RED = '#FFFFFF';

  // per-message dwell (ms) — readable lines hold longer than punchy ones
  var D_GREET = 5500, D_CONV = 7500, D_TRUST = 7000, D_SHIP = 6800,
      D_SOCIAL = 6800, D_HELP = 8000, D_INTEL = 8000, D_CONTEXT = 6200, D_NUDGE = 6200;

  function lsGet(k, d) { try { var v = localStorage.getItem(k); return v === null ? d : v; } catch (e) { return d; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function ssGet(k, d) { try { var v = sessionStorage.getItem(k); return v === null ? d : v; } catch (e) { return d; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  function jParse(s, d) { try { return JSON.parse(s); } catch (e) { return d; } }
  function money(c) { var d = Math.max(0, c) / 100; return '$' + (Number.isInteger(d) ? d : d.toFixed(2)); }
  function SH() { return window.TPWStoreHours; }

  var BLOCKS = {
    genuineFull: '100% genuine stock, shipped from our Melbourne store.',
    genuineTight: '100% genuine, shipped from Melbourne.',
    genuineChecked: 'Genuine stock, checked by our Melbourne team.'
  };

  /* ---- the converter line (dispatch-aware; red countdown when same-day) --- */
  function converter() {
    var S = SH();
    if (!S) return 'Free shipping over $150.';
    var d = S.dispatchETA();
    if (d.sameDay) {
      var secs = d.secondsToCutoff;
      if (secs <= 3600) { var mm = Math.max(1, Math.ceil(secs / 60)); return 'Last hour. Order in *' + mm + 'm* and it ships today.'; }
      var h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
      return 'Order in the next *' + h + 'h ' + m + 'm* and it ships today.';
    }
    if (S.isPublicHoliday()) return 'Open today. Online orders ship the next business day.';
    if (d.relative === 'tomorrow') return 'Order now and it ships first thing tomorrow.';
    return 'Order now and it ships ' + S.fmtWeekdayLong(d.shipParts) + '.';
  }
  function dispatchShort() {
    var S = SH(); if (!S) return 'shipped fast';
    var d = S.dispatchETA();
    return d.sameDay ? 'it ships today' : (d.relative === 'tomorrow' ? 'it ships tomorrow' : 'it ships ' + S.fmtWeekdayLong(d.shipParts));
  }
  function openLine() {
    var S = SH(); if (!S) return BLOCKS.genuineTight;
    var os = S.openStatus();
    if (os.open) return 'Open until ' + os.closeLabel + ' today, here in Melbourne.';
    return 'Order online anytime, shipped from Melbourne.';
  }

  /* ---- product intelligence: one true line per product_type (Tier 1) ------
     pi (Tier 2, a spec line with real numbers) is pre-resolved in Liquid and
     wins when present; otherwise we fall back to a safe generic, or null. */
  function piLine(typeStr, pi) {
    if (pi) return pi;
    var t = (typeStr || '').toLowerCase();
    if (!t) return null;
    if (/phunnel/.test(t)) return 'One centre hole keeps the juices in for a longer session.';
    if (/bowl/.test(t)) return 'Classic multi-hole bowl for even heat across the tobacco.';
    if (/starter|kit/.test(t)) return 'Everything you need to set up and get started.';
    if (/portable/.test(t)) return 'Compact and packs down, ready to take anywhere.';
    if (/traditional/.test(t) && /(shisha|pipe|hookah|stem)/.test(t)) return 'A traditional pipe, made the way it’s always been.';
    if (/(shisha|pipe|hookah|stem)/.test(t)) return 'A modern pipe built for an easy, smooth session.';
    if (/hmd|heat manage|provost|lotus|kaloud/.test(t)) return 'Manages your coals on top, so no foil needed.';
    if (/hex/.test(t) && /(coal|charcoal)/.test(t)) return 'Hexagon coconut coals, long-burning and easy to light.';
    if (/cylinder/.test(t) && /(coal|charcoal)/.test(t)) return 'Cylinder coconut coals for a clean, even burn.';
    if (/coal|charcoal/.test(t)) return 'Coconut coals for a clean, long, steady burn.';
    if (/hose/.test(t)) return 'Built for smooth, easy draws every time.';
    if (/foil/.test(t)) return 'Pre-cut foil for a quick, simple setup.';
    if (/mouth|tip/.test(t)) return 'Your own mouthpiece, hygienic and easy to carry.';
    if (/tong/.test(t)) return 'Handles hot coals safely from heat to bowl.';
    return null;
  }

  /* ---- line parser: *red* numbers, auto paper-mono numbers, cascade words */
  var NUM_RE = /\$?\d+(?:\.\d+)?(?:am|pm|h|m|%)?/g;
  function autoTokens(seg, out) {
    var last = 0, mt;
    NUM_RE.lastIndex = 0;
    while ((mt = NUM_RE.exec(seg))) {
      if (mt.index > last) out.push({ t: seg.slice(last, mt.index) });
      out.push({ t: mt[0], m: 1 });
      last = mt.index + mt[0].length;
    }
    if (last < seg.length) out.push({ t: seg.slice(last) });
  }
  function parseLine(str) {
    var tokens = [], parts = String(str).split('*');
    for (var i = 0; i < parts.length; i++) {
      var seg = parts[i];
      if (seg === '') continue;
      if (i % 2 === 1) tokens.push({ t: seg, m: 1, red: 1 });
      else autoTokens(seg, tokens);
    }
    return tokens;
  }

  var CSS =
    ':host{display:block;height:100%}' +
    '.wrap{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#141414;overflow:hidden}' +
    '.wrap.tap{cursor:pointer}' +
    '.dot{position:absolute;left:18px;top:50%;width:5px;height:5px;border-radius:50%;background:#F6F4EE;opacity:.28;transform:translateY(-50%);animation:gbpulse 2.8s ease-in-out infinite}' +
    '@keyframes gbpulse{0%,100%{opacity:.18}50%{opacity:.42}}' +
    '.lw{position:relative;height:24px;width:100%;overflow:hidden;-webkit-mask-image:linear-gradient(90deg,transparent,#000 7%,#000 93%,transparent);mask-image:linear-gradient(90deg,transparent,#000 7%,#000 93%,transparent)}' +
    '.layer{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;white-space:nowrap}' +
    '.msg{white-space:nowrap;display:inline-block;padding:0 26px}' +
    '.word{display:inline-block;overflow:hidden;vertical-align:top;height:20px;line-height:20px}' +
    '.winner{display:inline-block;line-height:20px;will-change:transform;font-family:\'Outfit\',system-ui,sans-serif;font-weight:400;font-size:13.5px;letter-spacing:.012em;color:#F6F4EE}' +
    '.tap .winner u{text-decoration:none;border-bottom:1px solid rgba(246,244,238,.45);padding-bottom:1px}' +
    '.mono{font-family:\'DM Mono\',\'Outfit\',ui-monospace,monospace;font-weight:500;font-size:13px;letter-spacing:.01em;color:#F6F4EE;display:inline-flex;align-items:center;height:20px;font-variant-numeric:tabular-nums}' +
    /* B-05: the reel clip window and each cell are both exactly 20px, so there is
       zero tolerance for the sub-pixel seam between the overflow:hidden clip (on
       .reel) and the transformed compositor layer (.reelInner has will-change).
       Mid-spin, that seam exposed a ~1px sliver of the neighbouring digit at the
       top/bottom edge — read as a stray superscript fragment (e.g. "²98.80").
       contain:paint pins the clip to .reel's own box on the same rasterisation
       grid (kills the compositor seam), and .cell overflow:hidden guarantees no
       glyph ever paints outside its own 20px box even if line metrics overflow. */
    '.reel{display:inline-block;height:20px;line-height:20px;overflow:hidden;contain:paint;vertical-align:top;width:.62em;text-align:center}' +
    '.reelInner{display:block;will-change:transform}' +
    '.cell{display:block;height:20px;line-height:20px;text-align:center;overflow:hidden}' +
    '.lit{display:inline-block}' +
    '.mono.red{font-weight:600}' +
    '.mono.red .reelInner{color:' + RED + '}' +
    '.sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);clip-path:inset(50%)}' +
    '@media (max-width:600px){.winner{font-size:12px}.mono{font-size:11.5px}.msg{padding:0 18px}.dot{left:12px}}' +
    '@media (prefers-reduced-motion:reduce){.dot{animation:none}}';

  class GeniusBar extends HTMLElement {
    connectedCallback() {
      if (window.__tpwGBInstance && window.__tpwGBInstance !== this) { this.style.display = 'none'; return; }
      window.__tpwGBInstance = this;
      this._listeners = [];
      this._total = 0; this._items = 0; this._itemMap = {}; this._primed = false;
      this._holding = false; this._busy = false; this._pending = null; this._slot = -1; this._cur = null;
      this._curDwell = D_CONV; this._tap = null; this._tapUrl = null; this._rotT = null; this._holdT = null; this._pollT = null;
      this._reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._page = this.getAttribute('data-page') || 'other';
      this._category = (this.getAttribute('data-category') || '').trim();
      this._searchEmpty = this.getAttribute('data-search-empty') === 'true';
      this._ptitle = (this.getAttribute('data-product-title') || '').trim();
      this._pi = piLine(this.getAttribute('data-product-type'), (this.getAttribute('data-pi') || '').trim());
      this._contact = this.getAttribute('data-contact') || '/pages/contact-us';
      this._phone = this.getAttribute('data-phone') || '';
      this._wa = (this.getAttribute('data-wa') || '').replace(/[^0-9]/g, '');
      this._sister = this.getAttribute('data-sister') || ''; /* TPS fallback removed 2026-07-28 - see snippets/header-sister-link.liquid */
      this._promoCode = (this.getAttribute('data-promo-code') || '').trim();
      this._promoPct = (this.getAttribute('data-promo-pct') || '').trim();

      var firstThisSession = !ssGet('__tpw_gb_seen', '');
      var visits = parseInt(lsGet('__tpw_visits', '0'), 10) || 0;
      if (firstThisSession) { visits++; lsSet('__tpw_visits', String(visits)); ssSet('__tpw_gb_seen', '1'); }
      this._visit = visits <= 1 ? 'first' : 'return';
      this._firstSession = firstThisSession;
      this._promoShown = ssGet('__tpw_gb_promo', '') === '1';

      this._hideFallback();
      this._loadFonts();
      this._build();
      this._primeFromAttrs();
      this._installHooks();
      this._startPolling();
      this._setupVisibility();
      this._kickoff();
      this._refreshCart();
    }
    disconnectedCallback() {
      if (window.__tpwGBInstance === this) window.__tpwGBInstance = null;
      if (this._rotT) clearTimeout(this._rotT);
      if (this._holdT) clearTimeout(this._holdT);
      if (this._pollT) clearInterval(this._pollT);
      if (this._cartUnsub) { try { this._cartUnsub(); } catch (e) {} this._cartUnsub = null; }
      if (this._countObs) { try { this._countObs.disconnect(); } catch (e) {} }
      (this._listeners || []).forEach(function (l) { try { l.t.removeEventListener(l.e, l.f, l.o); } catch (e) {} });
      this._listeners = [];
    }
    _on(t, e, f, o) { t.addEventListener(e, f, o || false); this._listeners.push({ t: t, e: e, f: f, o: o || false }); }

    _hideFallback() {
      try { var fb = document.getElementById('tpw-gb-fallback'); if (fb) { fb.setAttribute('aria-hidden', 'true'); fb.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);clip-path:inset(50%)'; } } catch (e) {}
    }
    _loadFonts() {
      /* TPW fonts (Outfit + DM Mono) are now self-hosted globally via
         snippets/tpw-fonts.liquid. Document-level @font-face cascades into this
         shadow root, so no third-party Google Fonts load is needed here. */
    }
    _build() {
      var self = this;
      var root = this.attachShadow({ mode: 'open' });
      var st = document.createElement('style'); st.textContent = CSS; root.appendChild(st);
      var w = document.createElement('div'); w.className = 'wrap';
      w.innerHTML = '<span class="dot" aria-hidden="true"></span>' +
        '<div class="lw" id="lw" aria-hidden="true"></div>' +
        '<span class="sr">The Premium Way. Genuine shisha gear, shipped from our Melbourne store. Free shipping over $150, then $9.95 flat. Same-day dispatch on orders before 2pm.</span>';
      root.appendChild(w);
      this._wrap = w; this._lw = w.querySelector('#lw');
      this._on(w, 'click', function () { self._onTap(); });
      this._on(this, 'keydown', function (e) { if ((e.key === 'Enter' || e.key === ' ') && self._tapUrl) { e.preventDefault(); self._onTap(); } });
    }
    _onTap() {
      var u = this._tapUrl; if (!u) return;
      if (/^https?:/.test(u)) window.open(u, '_blank', 'noopener'); else window.location.href = u;
    }

    /* ---- cart engine (ported from tpw-bar.js, with item diff) ----------- */
    _primeFromAttrs() {
      /* FL-02 (2026-07-03): the server-rendered data-cart-total/items are fresh truth
         for THIS page load — they must always win. The localStorage cache used to
         override them, so a stale total from an earlier session painted the wrong
         money until /cart.js came back. Cache now only fills a field when its SSR
         attr is absent/unparsable, and we re-save the SSR values so it self-heals. */
      var t = parseInt(this.getAttribute('data-cart-total'), 10), i = parseInt(this.getAttribute('data-cart-items'), 10);
      if (!isNaN(t)) this._total = t; if (!isNaN(i)) this._items = i;
      if (!isNaN(t) && !isNaN(i)) { this._saveCache(); return; }
      var raw = lsGet(STORAGE_KEY, ''); var d = raw && jParse(raw, null);
      if (d && Date.now() - d.t < STORAGE_TTL) {
        if (isNaN(t)) this._total = d.total || this._total;
        if (isNaN(i)) this._items = d.items || this._items;
      }
    }
    _saveCache() { lsSet(STORAGE_KEY, JSON.stringify({ total: this._total, items: this._items, t: Date.now() })); }
    _refreshCart() {
      var self = this;
      fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c) self._applyCart(c); }).catch(function () {});
    }
    _applyCart(cart) {
      var total = cart.total_price || 0, items = cart.item_count || 0, arr = cart.items || [];
      var newMap = {}; arr.forEach(function (it) { newMap[it.id] = it.quantity; });
      var added = false, removed = false;
      if (this._primed) {
        for (var id in newMap) { if (newMap[id] > (this._itemMap[id] || 0)) { added = true; break; } }
        if (!added) { for (var pid in this._itemMap) { if ((newMap[pid] || 0) < this._itemMap[pid]) { removed = true; break; } } }
      }
      var wasItems = this._items;
      this._total = total; this._items = items; this._itemMap = newMap; this._saveCache();
      if (!this._primed) { this._primed = true; return; }
      if (added) this._react(this._total >= THRESHOLD ? 'Free shipping unlocked. You’re all set.' : 'Added to your cart.');
      else if (removed) this._react('Removed. It’s here when you want it.');
      else if (wasItems !== items) this._renderState();   // empty<->filled transition: re-resolve
    }
    _installHooks() {
      var self = this, deb = null;
      var refresh = function (delay) { if (deb) return; deb = setTimeout(function () { deb = null; self._refreshCart(); }, delay || DEBOUNCE_MS); };

      // B-14 (2026-07-01): the genius bar no longer monkey-patches window.fetch (or
      // XHR / PerformanceObserver) to notice cart mutations. Routing every page fetch
      // through a wrapper was latent fragility and a suspected source of "Failed to
      // fetch" noise. Instead we listen for cart change the theme's OWN way, on two
      // complementary signals, then re-read /cart.js ONCE in the handler:
      //
      //  1. The theme pub/sub `PUB_SUB_EVENTS.cartUpdate` (assets/pubsub.js +
      //     constants.js). Published by product-form.js (PDP add-to-cart) and
      //     cart.js CartItems (drawer qty +/- and remove). We subscribe if the
      //     global publisher/subscriber exist. Also keep the legacy DOM CustomEvent
      //     names below for any app/theme path that dispatches those instead.
      //  2. A MutationObserver on the STABLE `.shopify-section` ancestor of
      //     #cart-icon-bubble. This is the universal, path-agnostic tell borrowed
      //     from tpw-cart-roll.js: EVERY cart mutation — including the product-card /
      //     recommendations instant-add (pdp-recs-quickadd.js → CartDrawer.render
      //     Contents), which does NOT publish cartUpdate — re-renders the
      //     cart-icon-bubble section and swaps that element wholesale. We must watch
      //     the SECTION wrapper (which survives the swap), not #cart-icon-bubble
      //     itself (an observer bound to the replaced node goes deaf). This is what
      //     preserves the free-ship / cart-total update on the card + recs add paths
      //     without any fetch wrapper.

      // Signal 1a: theme pub/sub (preferred; the instant-cart system's own channel).
      try {
        var subFn = window.subscribe || (typeof subscribe === 'function' ? subscribe : null);
        var evt = (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate)
          || (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate)
          || 'cart-update';
        if (typeof subFn === 'function') {
          this._cartUnsub = subFn(evt, function () { refresh(80); });
        }
      } catch (e) {}

      // Signal 1b: legacy DOM CustomEvents (kept for external apps / other paths).
      ['cart:updated', 'cart:refresh', 'cart:added', 'cart:changed', 'cart:update', 'cart-update', 'theme:cart:updated', 'on:cart:updated', 'ajaxCart.afterCartLoad', 'product:added-to-cart', 'dawn:cart:updated']
        .forEach(function (e) { var h = function () { refresh(80); }; self._on(document, e, h); self._on(window, e, h); });

      // Signal 2: universal cart-icon-bubble section re-render observer.
      var attach = function () {
        if (!self._countObs) self._countObs = new MutationObserver(function () { refresh(80); });
        var bubble = document.getElementById('cart-icon-bubble');
        var watchEl = bubble ? (bubble.closest('.shopify-section') || bubble.parentElement || bubble) : null;
        if (watchEl && !watchEl.__tpwGBObserved) {
          watchEl.__tpwGBObserved = true;
          try { self._countObs.observe(watchEl, { childList: true, characterData: true, subtree: true }); } catch (e) {}
        }
      };
      attach(); setTimeout(attach, 1500); setTimeout(attach, 4000);
    }
    _startPolling() { var self = this; this._pollT = setInterval(function () { if (!document.hidden) self._refreshCart(); }, POLL_MS); }
    _setupVisibility() { var self = this; this._on(document, 'visibilitychange', function () { if (!document.hidden) self._refreshCart(); }); }

    /* ---- state machine -------------------------------------------------- */
    _isMobile() { return window.matchMedia && window.matchMedia('(max-width:600px)').matches; }
    _M(desk, mob) { return this._isMobile() ? mob : desk; }
    _e(f, d, tap) { return { f: (typeof f === 'function' ? f : function () { return f; }), d: d, tap: tap || null }; }
    _promoEntry() {
      if (!this._promoCode || !this._promoPct || this._promoShown || this._visit !== 'return') return null;
      var self = this;
      return this._e(function () { return 'A little welcome back: *' + self._promoPct + '%* off with ' + self._promoCode + '.'; }, 8000, 'incentive');
    }
    _deck() {
      var self = this, e = function (f, d, tap) { return self._e(f, d, tap); };
      var deck;
      switch (this._page) {
        case 'home':
          deck = this._visit === 'first'
            // OUR STORY (first landing) — Cicero, compliance-vetted. "licensed" CUT
            // (implies tobacco licence = sister store). Worldwide/signature pending
            // founder confirm -> safe Australia-only line for now. Converter at #3
            // keeps the one red mark early.
            ? [e('Welcome to The Premium Way.', 5500),
               e(function () { return self._M('A real Melbourne shisha store, not a drop-shipper.', 'A real Melbourne shisha store.'); }, 7000),
               e(converter, D_CONV),
               e(function () { return self._M('We only sell genuine gear. If it isn’t genuine, we don’t stock it.', '100% genuine, or we don’t stock it.'); }, 7500),
               e(function () { return self._M('Never grey-imported. Ever.', 'Never grey-imported.'); }, 6200),
               e(function () { return self._M('We ship across Australia and worldwide.', 'Australia and worldwide.'); }, 6800),
               e(function () { return self._M('Tracked, and signed for on arrival.', 'Tracked and signed for.'); }, 6500),
               e(function () { return self._M('Packed properly, so it arrives the way it left.', 'Packed properly, every time.'); }, 6200),
               e(function () { return self._M('Rated 5.0 from 250+ Google reviews.', '5.0 from 250+ reviews.'); }, 6800),
               e(function () { return self._M('Buy now, pay later with Zip, Klarna or PayPal.', 'Pay later: Zip, Klarna, PayPal.'); }, 7000),
               e(function () { return self._M('Free shipping over $150, then $9.95 flat.', 'Free over $150, $9.95 under.'); }, 6800)]
            : [e('Welcome back.', D_GREET), e(converter, D_CONV), e(openLine, D_TRUST),
               e('Rated 5.0 from 250+ Google reviews.', D_SOCIAL),
               e('Questions? Message our Melbourne team.', D_HELP, 'help')];
          break;
        case 'collection':
          deck = [e(function () { return self._M(self._category ? 'You’re browsing ' + self._category + '.' : 'You’re browsing our shisha range.', 'You’re browsing our shisha range.'); }, D_CONTEXT),
                  e(function () { return self._M(self._category ? 'Questions about ' + self._category + '? Ask our Melbourne team.' : 'Questions? Ask our Melbourne team.', 'Questions? Ask our Melbourne team.'); }, D_HELP, 'help'),
                  e(converter, D_CONV), e('Every piece genuine, checked by our team.', D_TRUST),
                  e('Free shipping over $150.', D_SHIP)];
          break;
        case 'product':
          deck = [e(converter, D_CONV), e(BLOCKS.genuineChecked, D_TRUST),
                  e('Not sure it’s right for you? Ask us.', D_HELP, 'help'),
                  e('Free shipping over $150.', D_SHIP)];
          if (this._pi) deck.unshift(e(this._pi, D_INTEL));
          break;
        case 'search':
          deck = this._searchEmpty
            ? [e(function () { return self._M('No matches yet. Tell us what you’re after and we’ll find it.', 'No matches yet. Tell us what you need.'); }, D_HELP, 'help'),
               e('We dispatch fast, 7 days a week.', D_SHIP)]
            : [e('Here’s what we found for you.', D_CONTEXT), e(converter, D_CONV),
               e('100% genuine, shipped from Melbourne.', D_TRUST)];
          break;
        case 'cart':
          deck = [e('Your cart’s empty. Let’s sort that.', D_NUDGE), e(converter, D_CONV)];
          break;
        case '404':
          deck = [e('That page has gone. Let’s get you back on track.', D_CONTEXT),
                  e('Lost? We’ll point you the right way.', D_HELP, 'help'),
                  e('We dispatch fast, 7 days a week.', D_SHIP)];
          break;
        case 'thankyou':
          deck = [e('Thank you. Your order’s confirmed.', D_GREET),
                  e(function () { return 'Shipping from our Melbourne store, ' + dispatchShort() + '.'; }, D_TRUST),
                  e('Next time you need bowls, hoses or charcoal, we’ve got you.', D_TRUST)];
          break;
        default:
          deck = [e(function () { return self._M(BLOCKS.genuineFull, BLOCKS.genuineTight); }, D_TRUST),
                  e(converter, D_CONV), e('Free shipping over $150.', D_SHIP),
                  e('Questions? Message our Melbourne team.', D_HELP, 'help')];
      }
      var promo = this._promoEntry();
      if (promo && this._page !== 'thankyou' && this._page !== 'cart') deck.unshift(promo);
      return deck;
    }
    _pinEntry() {
      var self = this;
      return this._e(function () {
        var t = self._total;
        if (t >= THRESHOLD) return 'Free shipping unlocked. You’re all set.';
        var gap = THRESHOLD - t;
        if (gap <= NEAR) return 'Just *' + money(gap) + '* more and shipping’s free.';
        return 'You’re only *' + money(gap) + '* away from free shipping.';
      }, D_CONV, null);
    }
    _resolveTap(tap) {
      if (tap === 'sister') return this._sister;
      if (tap === 'incentive') return this._promoCode ? ('/discount/' + encodeURIComponent(this._promoCode)) : this._contact;
      // help
      if (this._wa) {
        var msg = this._page === 'product' && this._ptitle ? ('Hi, I have a question about ' + this._ptitle + '.')
          : this._page === 'collection' && this._category ? ('Hi, I’m looking at your ' + this._category + ' range and have a question.')
          : 'Hi, I have a question.';
        return 'https://wa.me/' + this._wa + '?text=' + encodeURIComponent(msg);
      }
      return this._contact;
    }
    _kickoff() {
      if (this._firstSession && this._visit === 'return' && this._items > 0) { this._react('Welcome back. Your cart’s still here.', RESUME_MS); return; }
      this._renderState();
    }
    _renderState() {
      if (this._rotT) { clearTimeout(this._rotT); this._rotT = null; }
      if (this._items > 0) { this._renderEntry(this._pinEntry()); return; }   // pinned, no rotation
      this._slot = 0;
      this._renderEntry(this._deck()[0]);
      this._scheduleNext();
    }
    _scheduleNext() {
      var self = this; if (this._rotT) clearTimeout(this._rotT);
      this._rotT = setTimeout(function () { self._advance(); }, this._curDwell || D_CONV);
    }
    _advance() {
      if (document.hidden || this._holding) { this._scheduleNext(); return; }
      if (this._items > 0) return;
      var deck = this._deck();
      this._slot = (this._slot + 1) % deck.length;
      if (deck[this._slot].tap === 'incentive') { this._promoShown = true; ssSet('__tpw_gb_promo', '1'); }
      this._renderEntry(deck[this._slot]);
      this._scheduleNext();
    }
    _renderEntry(entry) {
      this._curDwell = entry.d;
      this._tap = entry.tap;
      this._tapUrl = entry.tap ? this._resolveTap(entry.tap) : null;
      var line = entry.f();
      if (entry.tap) {
        this._wrap.classList.add('tap');
        this.setAttribute('role', 'link'); this.setAttribute('tabindex', '0');
        this.setAttribute('aria-label', line.replace(/\*/g, '') + (entry.tap === 'sister' ? ', visit The Premium Shisha' : ', message our Melbourne team'));
      } else {
        this._wrap.classList.remove('tap');
        this.removeAttribute('role'); this.removeAttribute('tabindex'); this.removeAttribute('aria-label');
      }
      this._render(line);
    }
    _react(line, ms) {
      var self = this; this._holding = true;
      if (this._rotT) { clearTimeout(this._rotT); this._rotT = null; }
      this._tap = null; this._tapUrl = null; this._wrap.classList.remove('tap');
      this.removeAttribute('role'); this.removeAttribute('tabindex'); this.removeAttribute('aria-label');
      this._render(line);
      if (this._holdT) clearTimeout(this._holdT);
      this._holdT = setTimeout(function () { self._holding = false; self._renderState(); }, ms || REACT_MS);
    }

    /* ---- rendering: word cascade + odometer reels ----------------------- */
    _layerHTML(tokens) {
      var units = [], tapHint = !!this._tap;
      function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
      function unit(inner) { return '<span class="word"><span class="winner" style="transform:translateY(116%)">' + inner + '</span></span>'; }
      function monoHTML(text) {
        var out = '';
        for (var i = 0; i < text.length; i++) {
          var c = text[i];
          if (/[0-9]/.test(c)) out += '<span class="reel" data-d="' + c + '"><span class="reelInner"></span></span>';
          else out += '<span class="lit">' + (c === ' ' ? '&nbsp;' : esc(c)) + '</span>';
        }
        return out;
      }
      tokens.forEach(function (tk) {
        if (tk.t === '' || tk.t == null) return;
        if (tk.m) { units.push(unit('<span class="mono' + (tk.red ? ' red' : '') + '">' + monoHTML(tk.t) + '</span>')); return; }
        tk.t.split(/(\s+)/).forEach(function (seg) {
          if (seg === '') return;
          if (/^\s+$/.test(seg)) units.push('&nbsp;');
          else units.push(unit(tapHint ? '<u>' + esc(seg) + '</u>' : esc(seg)));
        });
      });
      return '<span class="msg">' + units.join('') + '</span>';
    }
    _makeLayer(tokens) { var d = document.createElement('div'); d.className = 'layer'; d.innerHTML = this._layerHTML(tokens); return d; }
    _rollReels(winner) {
      [].forEach.call(winner.querySelectorAll('.reel'), function (reel, col) {
        var target = parseInt(reel.getAttribute('data-d'), 10);
        var inner = reel.querySelector('.reelInner');
        var steps = 11 + (col % 4), seq = [];
        for (var k = 0; k < steps; k++) seq.push((target - (steps - 1) + k + 100) % 10);
        inner.innerHTML = seq.map(function (n) { return '<span class="cell">' + n + '</span>'; }).join('');
        inner.style.transform = 'translateY(0)';
        var dist = (seq.length - 1) * 20;
        // P-018: once the spin settles, collapse the reel back to its single target
        // cell so no neighbouring spin cell can bleed through the .reel overflow clip
        // at rest (a tight clip window / sub-pixel seam shows a faint doubled digit).
        // Leaving the full ~11-cell sequence stacked at rest is what the cart-total /
        // shared roller showed as a ghosted trailing digit; collapse for parity here.
        function settle(e) {
          if (e && e.propertyName && e.propertyName !== 'transform') return;
          inner.removeEventListener('transitionend', settle);
          if (inner.querySelectorAll('.cell').length > 1) {
            inner.style.transition = 'none';
            inner.innerHTML = '<span class="cell">' + target + '</span>';
            inner.style.transform = 'translateY(0)';
          }
        }
        inner.addEventListener('transitionend', settle);
        requestAnimationFrame(function () { setTimeout(function () {
          inner.style.transition = 'transform .74s cubic-bezier(.16,1,.3,1)';
          inner.style.transform = 'translateY(-' + dist + 'px)';
          // P-018 hardening (Argus 2026-06-30): transitionend is dropped in
          // backgrounded/throttled tabs and under rAF starvation (e.g. near the footer
          // Google Map), leaving the reel un-collapsed and ghosting at rest. Fall back to
          // a timer (> the .74s transition) so settle() always runs. settle() is
          // idempotent, so a double-fire with transitionend is a harmless no-op.
          setTimeout(settle, 900);
        }, col * 50); });
      });
    }
    _render(str) {
      if (this._busy) { this._pending = str; return; }
      var tokens = parseLine(str);
      var nl = this._makeLayer(tokens);
      var old = this._cur, self = this;

      if (this._reduced) {
        if (old) old.remove();
        this._lw.appendChild(nl);
        [].forEach.call(nl.querySelectorAll('.winner'), function (w) { w.style.transform = 'translateY(0)'; });
        [].forEach.call(nl.querySelectorAll('.reel'), function (r) { r.querySelector('.reelInner').innerHTML = '<span class="cell">' + r.getAttribute('data-d') + '</span>'; });
        this._cur = nl;
        return;
      }

      this._busy = true;
      this._lw.appendChild(nl);
      this._cur = nl;
      if (old) { old.style.transition = 'opacity .3s ease'; requestAnimationFrame(function () { old.style.opacity = '0'; }); setTimeout(function () { old.remove(); }, 320); }
      var winners = [].slice.call(nl.querySelectorAll('.winner'));
      winners.forEach(function (w, i) {
        setTimeout(function () {
          w.style.transition = 'transform .6s cubic-bezier(.16,1,.3,1)';
          w.style.transform = 'translateY(0)';
          if (w.querySelector('.reel')) self._rollReels(w);
        }, i * 45);
      });
      setTimeout(function () {
        self._busy = false;
        if (self._pending != null) { var s = self._pending; self._pending = null; self._render(s); }
      }, winners.length * 45 + 820);
    }
  }

  function def() { if (!customElements.get('tpw-genius-bar')) customElements.define('tpw-genius-bar', GeniusBar); }
  if (document.readyState !== 'loading') def(); else document.addEventListener('DOMContentLoaded', def);
})();
